<template>
  <v-row class="ma-0">
    <v-col class="pa-0">
      <!-- Prices -->
      <v-row v-if="!hidePriceDetails" class="ma-0 pa-4 pb-0">
        <v-col class="pa-0 my-auto fit-content">
          <v-row class="ma-0 token-text-medium token-text-color-primary">
            <PriceDisplay 
              :price="totalPriceMin" 
              :price2="totalPriceMax" 
              :currency="currency"
            />
          </v-row>
        </v-col>
        <v-col class="pa-0 my-auto ml-auto fit-content">
          <v-row class="ma-0">
            <div class="token-text-medium token-text-color-secondary">
              Price details
            </div>
            <CustomButton class="ml-2 my-auto" :level="$enums.ButtonLevels.SmallIcon" @click="priceDetailDeployed = !priceDetailDeployed">
              <Icon icon="caret-down" class="caret" :class="{invert : priceDetailDeployed}"/>
            </CustomButton>
          </v-row>
        </v-col>
      </v-row>
      <HeightDeployer v-model="priceDetailDeployed" class="full-width">
        <v-row class="ma-0 mx-4 token-text-regular">
          <v-col class="pa-0">
            <v-row class="ma-0">
              <v-col class="pa-0 my-auto fit-content">
                Worker nodes
              </v-col>
              <v-col class="pa-0 my-auto mx-2">
                <Divider/>
              </v-col>
              <v-col class="pa-0 ml-auto fit-content">
                <PriceDisplay 
                  :price="clusterNodeTypologies.length ? minPriceWorkerNodes : null"
                  :price2="maxPriceWorkerNodes" 
                  :currency="currency"
                  :disableBurger="true"
                />
              </v-col>
            </v-row>
            <v-row class="ma-0">
              <v-col class="pa-0 my-auto fit-content">
                <v-row class="ma-0">
                  <div>Control nodes</div>
                  <Tooltip class="ml-2" docLink="guides/cluster-configuration/#defining-the-number-of-nodes">
                    <template #header>
                      Control nodes are added
                    </template>
                    <template #content>
                      Starting at 2 nodes, we add dedicated nodes to manage the workload on your cluster.<br/>
                      They are added to improve reliability and stability and while fully managed by Fransys, they increase the overall cost of your cluster.
                    </template>
                  </Tooltip>
                </v-row>
              </v-col>
              <v-col class="pa-0 my-auto mx-2">
                <Divider/>
              </v-col>
              <v-col class="pa-0 ml-auto fit-content">
                <PriceDisplay 
                  :price="providerRequirements.length ? minPriceMasterNodes : null"
                  :price2="maxPriceMasterNodes" 
                  :currency="currency"
                  :disableBurger="true"
                />
              </v-col>
            </v-row>
            <v-row class="ma-0">
              <v-col class="pa-0 my-auto fit-content">
                <v-row class="ma-0">
                  <div>Load balancer</div>
                  <Tooltip class="ml-2">
                    <template #header>
                      A load balancer is added
                    </template>
                    <template #content>
                      Starting at 2 nodes, we add a load balancer in front of your cluster.<br/>
                      It is fully managed by Fransys and is here to better balance your traffic and improve reliability.
                    </template>
                  </Tooltip>
                </v-row>
              </v-col>
              <v-col class="pa-0 my-auto mx-2">
                <Divider/>
              </v-col>
              <v-col class="pa-0 ml-auto fit-content">
                <PriceDisplay 
                  :price="providerRequirements.length ? minPriceLoadBalancer : null" 
                  :price2="maxPriceLoadBalancer" 
                  :currency="currency"
                  :disableBurger="true"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </HeightDeployer>
      <!-- / Prices -->

      <v-col v-if="!hidePriceDetails" class="pa-0 mt-2 my-auto">
        <Divider/>
      </v-col>
      <v-row class="pa-2 ma-0 flex">
        <CustomButton 
          :level="$enums.ButtonLevels.Secondary"
          text="Provider / Location"
          icon="caret-left"  
          @click="$emit('previous')" 
        />
        <CustomButton class="ml-auto" :level="$enums.ButtonLevels.Primary" :text="'Deploy cluster'" :icon="'publish'" @click="$emit('publish')" :status="publishState" :loading="publishLoading" :disabled="publishDisabled" />
      </v-row>

    </v-col>
  </v-row>
</template>

<script lang="ts">
import { Vue, Options, prop } from 'vue-class-component'
import { APIClusterLocation, APIClusterNodeTypologie, APIClusterRequirement, APILoadBalancerTypologie } from '@/typesAPI';
import { NodePool } from '@/types';

import HeightDeployer from '@/components/UIElements/HeightDeployer.vue';
import CustomButton from '@/components/UIElements/CustomButton.vue'
import Divider from '@/components/UIElements/Divider.vue'
import PriceDisplay from '@/components/UIElements/PriceDisplay.vue'
import Tooltip from '@/components/UIElements/Tooltip.vue'
import Icon from '@/components/UIElements/Icon.vue'


class Props {
  isAutoPilot: boolean = prop({
    required: true,
  });
  provider: string = prop({
    required: true,
  });
  location: APIClusterLocation = prop({
    required: true,
  });
  nodePoolList: NodePool[] = prop({
    required: true,
  });
  masterNodes: {
    min: APIClusterNodeTypologie[];
    max: APIClusterNodeTypologie[];
  } = prop({
    required: true,
  });
  nbNodes: {
    min: number;
    max: number;
  } = prop({
    required: true,
  });
  publishDisabled?: boolean = prop({
    required: false,
  });
  publishLoading?: boolean = prop({
    required: false,
  });
  publishState?: string = prop({
    required: false,
  });
}


@Options({
  components: {
    HeightDeployer,
    CustomButton,
    Divider,
    PriceDisplay,
    Tooltip,
    Icon
  },
})
export default class MPFClusterConfig extends Vue.with(Props) {
  priceDetailDeployed = false;

  get hidePriceDetails(): boolean {
    return this.isAutoPilot && !this.nodePoolList.length
  }

  // Ressources
  get currency():string {
    return this.$store.getters['user/getCurrency']
  }

  get clusterNodeTypologies():APIClusterNodeTypologie[] {
    if(!this.provider) {
      return []
    }
    return this.$store.getters['clusters/getClusterNodeTypologiesByProviderAndLocation'](this.provider, this.location?.id)
  }

  get loadBalancerTypologies():APILoadBalancerTypologie[] {
    if(!this.provider) {
      return []
    }
    return this.$store.getters['clusters/getLoadBalancerTypologiesByProviderAndLocation'](this.provider, this.location?.id)
  }

  get providerRequirements ():APIClusterRequirement[] {
    if(!this.location) {
      return []
    }
    return this.$store.getters['clusters/getClusterRequirementsByProviderAndLocation'](this.provider, this.location.id)
  }

  // Nb load balancer
  get minLoadBalancer ():APILoadBalancerTypologie | null {
    const typoRequirement = this.$helpers.clusters.getTypologiesFromRequirements(this.providerRequirements, this.loadBalancerTypologies, this.nbNodes.min, this.$enums.RequirementTypes.LOAD_BALANCER) as APILoadBalancerTypologie[]
    return typoRequirement.length ? typoRequirement[0] : null
  }

  get maxLoadBalancer ():APILoadBalancerTypologie | null {
    const typoRequirement = this.$helpers.clusters.getTypologiesFromRequirements(this.providerRequirements, this.loadBalancerTypologies, this.nbNodes.max, this.$enums.RequirementTypes.LOAD_BALANCER) as APILoadBalancerTypologie[]
    return typoRequirement.length ? typoRequirement[0] : null
  }

  // Prices
  get minPriceLoadBalancer():number {
    let ret = 0
    if(this.minLoadBalancer && this.location?.id) {
      ret = parseInt(this.minLoadBalancer.attributes.price?.amount)
    }
    return ret
  }

  get minPriceMasterNodes():number {
    let ret = 0
    if(this.location?.id) {
      ret = this.masterNodes.min.length ? this.masterNodes.min.length * parseInt(this.masterNodes.min[0].attributes.price?.amount) : 0
    }
    return ret
  }

  get minPriceWorkerNodes():number {
    let ret = 0
    // If we have a node pool, we can calculate the min price based on the min size of each node pool
    this.nodePoolList.forEach((nodePool:NodePool) => {
      const typology = this.clusterNodeTypologies.find((typology:APIClusterNodeTypologie) => {
        return typology.id === nodePool.typology
      })
      if(typology) {
        ret += (this.isAutoPilot ? nodePool.min_size : nodePool.target_size) * parseInt(typology?.attributes.price?.amount || '0')
      }
    })
    
    return ret
  }

  get maxPriceLoadBalancer():number {
    if(!this.isAutoPilot) {
      return this.minPriceLoadBalancer
    }
    let ret = 0
    if(this.maxLoadBalancer && this.location?.id) {
      ret = parseInt(this.maxLoadBalancer.attributes.price?.amount)
    }
    return ret
  }

  get maxPriceMasterNodes():number {
    if(!this.isAutoPilot) {
      return this.minPriceMasterNodes
    }
    let ret = 0
    if(this.location?.id) {
      ret = this.masterNodes.max.length ? this.masterNodes.max.length * parseInt(this.masterNodes.max[0].attributes.price?.amount) : 0
    }
    return ret
  }

  get maxPriceWorkerNodes():number {
    if(!this.isAutoPilot) {
      return this.minPriceWorkerNodes
    }
    let ret = 0
    // If we have a node pool, we can calculate the max price based on the max size of each node pool
    this.nodePoolList.forEach((nodePool:NodePool) => {
      const typology = this.clusterNodeTypologies.find((typology:APIClusterNodeTypologie) => {
        return typology.id === nodePool.typology
      })
      if(typology) {
        ret += nodePool.max_size * parseInt(typology?.attributes.price?.amount || '0')
      }
    })
    
    return ret
  }

  get totalPriceMin():number {
    return this.minPriceWorkerNodes + this.minPriceMasterNodes + this.minPriceLoadBalancer
  }

  get totalPriceMax():number {
    return this.maxPriceWorkerNodes + this.maxPriceMasterNodes + this.maxPriceLoadBalancer
  }


}
</script>
<style lang="scss" scoped >
@import '@/css/variables';
.caret {
  transition: transform 0.2s ease-in-out;
  &.invert {
    transform: rotate(-180deg);
  }
}
</style>